import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_AI_VEHICLE,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_GROUND,
  COLLISION_GROUP_STATIC,
  COLLISION_GROUP_DYNAMIC_PROP,
} from '../core/PhysicsWorld';
import { TrafficLight } from '../environment/TrafficLights';
import { AIPedestrian } from './AIPedestrian';

export interface ObstacleVehicle {
  position: THREE.Vector3;
  speedKmh: number;
  halfLength?: number;
  entity?: any;
}

export class AIVehicle {
  public mesh: THREE.Group;
  public position = new THREE.Vector3();
  public speedKmh: number = 30;
  public cruiseSpeedKmh: number = 30;
  public waypoints: THREE.Vector3[] = [];
  public currentWaypointIdx: number = 0;
  public isYielding: boolean = false;
  public isBraking: boolean = false;
  public blinkerState: 'left' | 'right' | 'off' = 'off';
  public lifetimeSeconds: number = 0;

  // Physics body in Cannon-es
  public body: CANNON.Body | null = null;
  public isExternalImpact: boolean = false;
  private impactRecoverTimer: number = 0;

  // Lighting & Turn Signal Meshes
  private taillightMat!: THREE.MeshStandardMaterial;
  private indicatorLeftMeshes: THREE.Mesh[] = [];
  private indicatorRightMeshes: THREE.Mesh[] = [];
  private blinkTimer: number = 0;
  private blinkOn: boolean = false;

  // Collision avoidance state
  public targetSpeedKmh: number = 30;
  private yieldWaitTimer: number = 0;
  private readonly PLAYER_DANGER_DIST = 14.0;   // m — start slowing smoothly
  private readonly PLAYER_STOP_DIST   =  6.5;   // m — full stop with safe buffer behind player

  constructor(colorHex: number, waypoints: THREE.Vector3[], physicsWorld?: PhysicsWorld) {
    this.waypoints = waypoints;
    this.position.copy(waypoints[0] || new THREE.Vector3());

    this.mesh = new THREE.Group();
    this.buildMesh(colorHex);
    this.mesh.position.copy(this.position);

    if (physicsWorld) {
      this.initPhysicsBody(physicsWorld);
    }
  }

  private initPhysicsBody(physicsWorld: PhysicsWorld) {
    // Kinematic traffic vehicle rigid body — maintains stable cruise without tipping
    const chassisShape = new CANNON.Box(new CANNON.Vec3(0.91, 0.45, 2.05));
    this.body = new CANNON.Body({
      type: CANNON.Body.KINEMATIC,
      material: physicsWorld.aiVehicleMaterial,
      collisionFilterGroup: COLLISION_GROUP_AI_VEHICLE,
      collisionFilterMask:
        COLLISION_GROUP_GROUND |
        COLLISION_GROUP_CHASSIS |
        COLLISION_GROUP_STATIC |
        COLLISION_GROUP_DYNAMIC_PROP,
    });
    this.body.addShape(chassisShape, new CANNON.Vec3(0, 0.46, 0));
    this.body.position.set(this.position.x, this.position.y, this.position.z);

    physicsWorld.world.addBody(this.body);
  }


  private buildMesh(colorHex: number) {
    const carMat = new THREE.MeshStandardMaterial({
      color: colorHex,
      metalness: 0.7,
      roughness: 0.25,
    });

    // 1. Aerodynamic lower chassis & body
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.82, 0.55, 4.1), carMat);
    body.position.y = 0.46;
    body.castShadow = true;
    body.receiveShadow = true;

    // 2. Cabin & Roof
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.58, 0.55, 2.2), carMat);
    cabin.position.set(0, 0.96, -0.2);
    cabin.castShadow = true;

    // 3. Dark Tinted Windows
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0x0f172a,
      roughness: 0.1,
      metalness: 0.9,
    });
    const frontWindshield = new THREE.Mesh(new THREE.PlaneGeometry(1.45, 0.58), glassMat);
    frontWindshield.position.set(0, 0.98, 0.91);
    frontWindshield.rotation.x = -0.45;
    const rearWindshield = new THREE.Mesh(new THREE.PlaneGeometry(1.42, 0.52), glassMat);
    rearWindshield.position.set(0, 0.98, -1.31);
    rearWindshield.rotation.x = 0.42;
    rearWindshield.rotation.y = Math.PI;

    // 4. Ultra-Bright Headlights (White Xenon/LED)
    const headlightMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0xffffff,
      emissiveIntensity: 2.5,
      roughness: 0.1,
    });
    const headL = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.14, 0.1), headlightMat);
    headL.position.set(-0.65, 0.52, 2.05);
    const headR = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.14, 0.1), headlightMat);
    headR.position.set(0.65, 0.52, 2.05);

    // 5. Red LED Taillights (dynamic brake light intensity)
    this.taillightMat = new THREE.MeshStandardMaterial({
      color: 0xef4444,
      emissive: 0xef4444,
      emissiveIntensity: 1.2,
      roughness: 0.1,
    });
    const tailL = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.14, 0.1), this.taillightMat);
    tailL.position.set(-0.65, 0.55, -2.05);
    const tailR = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.14, 0.1), this.taillightMat);
    tailR.position.set(0.65, 0.55, -2.05);

    // 5b. Amber Turn Indicators (Front & Rear Left/Right)
    // In vehicle local space (+Z forward, +Y up): +X is Left, -X is Right
    const indLeftMat = new THREE.MeshStandardMaterial({
      color: 0xf59e0b,
      emissive: 0xf59e0b,
      emissiveIntensity: 0.0,
      roughness: 0.2,
    });
    const indRightMat = new THREE.MeshStandardMaterial({
      color: 0xf59e0b,
      emissive: 0xf59e0b,
      emissiveIntensity: 0.0,
      roughness: 0.2,
    });
    const indGeom = new THREE.BoxGeometry(0.14, 0.12, 0.11);
    const indFL = new THREE.Mesh(indGeom, indLeftMat);
    indFL.position.set(0.84, 0.52, 2.05);
    const indRL = new THREE.Mesh(indGeom, indLeftMat);
    indRL.position.set(0.84, 0.55, -2.05);
    const indFR = new THREE.Mesh(indGeom, indRightMat);
    indFR.position.set(-0.84, 0.52, 2.05);
    const indRR = new THREE.Mesh(indGeom, indRightMat);
    indRR.position.set(-0.84, 0.55, -2.05);
    this.indicatorLeftMeshes = [indFL, indRL];
    this.indicatorRightMeshes = [indFR, indRR];

    // 6. Wheels with alloy rims
    const tireMat = new THREE.MeshStandardMaterial({ color: 0x18181b, roughness: 0.9 });
    const rimMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.8, roughness: 0.2 });
    const wGeom = new THREE.CylinderGeometry(0.32, 0.32, 0.22, 20);
    wGeom.rotateZ(Math.PI / 2);
    const rimGeom = new THREE.CylinderGeometry(0.20, 0.20, 0.23, 16);
    rimGeom.rotateZ(Math.PI / 2);

    const offsets = [
      [-0.88, 0.32, 1.25],
      [0.88, 0.32, 1.25],
      [-0.88, 0.32, -1.25],
      [0.88, 0.32, -1.25],
    ];
    offsets.forEach((off) => {
      const tire = new THREE.Mesh(wGeom, tireMat);
      const rim = new THREE.Mesh(rimGeom, rimMat);
      tire.position.set(off[0], off[1], off[2]);
      rim.position.set(off[0], off[1], off[2]);
      tire.castShadow = true;
      this.mesh.add(tire, rim);
    });

    this.mesh.add(
      body,
      cabin,
      frontWindshield,
      rearWindshield,
      headL,
      headR,
      tailL,
      tailR,
      indFL,
      indRL,
      indFR,
      indRR
    );
  }

  public update(
    deltaTime: number,
    playerPos?: THREE.Vector3,
    playerSpeedKmh: number = 0,
    trafficLights?: TrafficLight[],
    pedestrians?: AIPedestrian[],
    otherVehicles?: ObstacleVehicle[]
  ) {
    if (this.waypoints.length === 0) return;

    this.lifetimeSeconds += deltaTime;

    if (this.speedKmh > this.cruiseSpeedKmh) {
      this.cruiseSpeedKmh = this.speedKmh;
    }

    // ── Frame 0 / Initial Spawn Overlap Defense (< 1.0s lifetime only) ──────
    // If an NPC vehicle spawns directly inside / intersecting the player hull (< 1.8m center distance):
    if (this.lifetimeSeconds < 1.0 && playerPos && this.position.distanceTo(playerPos) < 1.8) {
      for (let i = 0; i < this.waypoints.length; i++) {
        const nextIdx = (this.currentWaypointIdx + i + 1) % this.waypoints.length;
        if (this.waypoints[nextIdx].distanceTo(playerPos) >= 15.0) {
          this.currentWaypointIdx = nextIdx;
          this.position.copy(this.waypoints[nextIdx]);
          this.mesh.position.copy(this.position);
          if (this.body) {
            this.body.position.set(this.position.x, this.position.y, this.position.z);
            this.body.velocity.set(0, 0, 0);
          }
          break;
        }
      }
    }

    let targetWp = this.waypoints[this.currentWaypointIdx];
    const distToWp = this.position.distanceTo(targetWp);

    if (distToWp < 3.0) {
      this.currentWaypointIdx = (this.currentWaypointIdx + 1) % this.waypoints.length;
      targetWp = this.waypoints[this.currentWaypointIdx];
    }

    const dir = targetWp.clone().sub(this.position).normalize();

    // ── Turn Indicator Calculation (18m before waypoint turn) ────────────────
    const distToCenter = Math.hypot(this.position.x, this.position.z);
    if (distToCenter > 17.5 && distToWp < 18.0 && this.waypoints.length >= 3) {
      const nextWp = this.waypoints[(this.currentWaypointIdx + 1) % this.waypoints.length];
      const nextDir = nextWp.clone().sub(targetWp).normalize();
      // Right-hand vector of dir is (-dir.z, 0, dir.x), so crossY > 0 is Left, < 0 is Right
      const crossY = dir.z * nextDir.x - dir.x * nextDir.z;
      if (crossY > 0.28) {
        this.blinkerState = 'left';
      } else if (crossY < -0.28) {
        this.blinkerState = 'right';
      } else {
        this.blinkerState = 'off';
      }
    } else {
      this.blinkerState = 'off';
    }

    // ── 0. Vehicle Ahead (Following & Queueing at Lights/Stops) ──────────────
    let vehicleAheadRequiresStop = false;
    let distToVehicleAhead = 999;
    let speedOfVehicleAhead = 0;
    let targetStopBuffer = 5.6;

    if (otherVehicles && otherVehicles.length > 0) {
      for (const other of otherVehicles) {
        if (other.entity === this || other.position === this.position) continue;
        const toOther = other.position.clone().sub(this.position);
        const alongDir = toOther.dot(dir);
        const lateral = Math.abs(-dir.z * toOther.x + dir.x * toOther.z);

        // Vehicle is in front of us along our path (between 0.8m and 22m) and in our lane (lateral < 2.3m)
        if (alongDir > 0.8 && alongDir < 22.0 && lateral < 2.3) {
          const requiredBuffer = 2.2 + (other.halfLength ?? 2.2) + 1.2;
          if (alongDir < distToVehicleAhead) {
            distToVehicleAhead = alongDir;
            speedOfVehicleAhead = other.speedKmh;
            targetStopBuffer = requiredBuffer;
            vehicleAheadRequiresStop = true;
          }
        }
      }
    }

    // ── 0b. Cross-Traffic Priority Yielding (Roundabout & Rechts-vor-Links) ──
    let crossTrafficRequiresStop = false;
    // Roundabout entry yield (StVO Zeichen 205 + 215)
    if (distToCenter > 17.5 && distToCenter < 25.0) {
      const toCenter = new THREE.Vector3(-this.position.x, 0, -this.position.z).normalize();
      if (toCenter.dot(dir) > 0.5) {
        if (playerPos && Math.hypot(playerPos.x, playerPos.z) < 17.5 && this.position.distanceTo(playerPos) < 18.0) {
          crossTrafficRequiresStop = true;
        }
        if (!crossTrafficRequiresStop && otherVehicles) {
          for (const other of otherVehicles) {
            if (other.entity === this) continue;
            if (Math.hypot(other.position.x, other.position.z) < 17.5 && this.position.distanceTo(other.position) < 18.0) {
              crossTrafficRequiresStop = true;
              break;
            }
          }
        }
      }
    }

    // Rechts-vor-Links unregulated intersection yield (§ 8 Abs. 1 StVO)
    if (!crossTrafficRequiresStop) {
      const rvlNodes = [
        { x: 50, z: 60 },
        { x: -50, z: 60 },
        { x: 50, z: -60 },
        { x: -50, z: -60 },
      ];
      const rightVec = new THREE.Vector3(-dir.z, 0, dir.x);
      for (const node of rvlNodes) {
        const toNode = new THREE.Vector3(node.x - this.position.x, 0, node.z - this.position.z);
        const distNode = toNode.length();
        if (distNode >= 6.5 && distNode <= 15.0 && toNode.clone().normalize().dot(dir) > 0.6) {
          if (playerPos) {
            const distPlayerNode = Math.hypot(playerPos.x - node.x, playerPos.z - node.z);
            const toPlayerRel = playerPos.clone().sub(this.position);
            if (distPlayerNode < 14.0 && toPlayerRel.dot(rightVec) > 2.0 && toPlayerRel.dot(dir) > -2.0) {
              crossTrafficRequiresStop = true;
              break;
            }
          }
          if (otherVehicles) {
            for (const other of otherVehicles) {
              if (other.entity === this) continue;
              const distOtherNode = Math.hypot(other.position.x - node.x, other.position.z - node.z);
              const toOtherRel = other.position.clone().sub(this.position);
              if (distOtherNode < 14.0 && toOtherRel.dot(rightVec) > 2.0 && toOtherRel.dot(dir) > -2.0) {
                crossTrafficRequiresStop = true;
                break;
              }
            }
          }
        }
      }
    }

    // ── 1. Traffic Light Evaluation ──────────────────────────────────────────
    let lightRequiresStop = false;
    let distToLight = 999;
    if (trafficLights && trafficLights.length > 0) {
      for (const tl of trafficLights) {
        if (tl.phase === 'RED' || tl.phase === 'RED_YELLOW') {
          const align = dir.dot(tl.approachDirection);
          if (align > 0.7) {
            const toTl = tl.group.position.clone().sub(this.position);
            const alongDir = toTl.dot(dir);
            const lateral = Math.abs(-dir.z * toTl.x + dir.x * toTl.z);
            if (alongDir > 1.5 && alongDir < 22.0 && lateral < 6.0) {
              lightRequiresStop = true;
              distToLight = Math.min(distToLight, alongDir);
            }
          }
        }
      }
    }

    // ── 2. Pedestrian Yielding Evaluation ───────────────────────────────────
    let pedRequiresStop = false;
    let distToPed = 999;
    if (pedestrians && pedestrians.length > 0) {
      for (const ped of pedestrians) {
        const toPed = ped.position.clone().sub(this.position);
        const alongDir = toPed.dot(dir);
        const lateral = Math.abs(-dir.z * toPed.x + dir.x * toPed.z);
        if (alongDir > 1.0 && alongDir < 16.0 && lateral < 2.8) {
          pedRequiresStop = true;
          distToPed = Math.min(distToPed, alongDir);
        }
      }
    }

    // ── 3. Player Proximity & Forward Collision Avoidance ───────────────────
    let dotFront = -1;
    let distToPlayer = 999;
    let isImminentAhead = false;
    let playerInMyCorridor = false;

    if (playerPos) {
      distToPlayer = this.position.distanceTo(playerPos);
      const toPlayer = playerPos.clone().sub(this.position);
      dotFront = toPlayer.dot(dir);
      const lateralDist = Math.abs(-dir.z * toPlayer.x + dir.x * toPlayer.z);

      const dangerDist = Math.max(this.PLAYER_DANGER_DIST, (this.cruiseSpeedKmh / 3.6) * 2.8);
      // ONLY yield to player if player is ahead of us: dotFront > 0.5
      playerInMyCorridor = dotFront > 0.5 && dotFront < dangerDist && lateralDist < 2.3;
      isImminentAhead = dotFront > 0.5 && distToPlayer < 4.8;
    }

    // ── Target Speed Resolution ─────────────────────────────────────────────
    let calculatedTargetSpeed = this.cruiseSpeedKmh;
    let mustFullStop = false;
    let isYieldingNow = false;

    if (crossTrafficRequiresStop) {
      isYieldingNow = true;
      mustFullStop = true;
      calculatedTargetSpeed = 0;
    }

    // Vehicle ahead target speed (queueing & following)
    if (vehicleAheadRequiresStop) {
      isYieldingNow = true;
      if (distToVehicleAhead <= targetStopBuffer) {
        mustFullStop = true;
        calculatedTargetSpeed = 0;
        if (distToVehicleAhead <= targetStopBuffer - 0.5) {
          this.speedKmh = 0;
        }
      } else {
        const t = Math.max(0, (distToVehicleAhead - targetStopBuffer) / (22.0 - targetStopBuffer));
        const matchedSpeed = Math.min(speedOfVehicleAhead, this.cruiseSpeedKmh);
        calculatedTargetSpeed = Math.min(calculatedTargetSpeed, Math.max(matchedSpeed, this.cruiseSpeedKmh * t));
        if (calculatedTargetSpeed < 3.0 && distToVehicleAhead <= targetStopBuffer + 1.2) {
          mustFullStop = true;
          calculatedTargetSpeed = 0;
        }
      }
    }

    // Traffic light target speed
    if (lightRequiresStop) {
      isYieldingNow = true;
      if (distToLight <= 6.5) {
        mustFullStop = true;
        calculatedTargetSpeed = 0;
      } else {
        const t = Math.max(0, (distToLight - 6.5) / (22.0 - 6.5));
        calculatedTargetSpeed = Math.min(calculatedTargetSpeed, this.cruiseSpeedKmh * (t * t));
        if (calculatedTargetSpeed < 3.0) {
          mustFullStop = true;
          calculatedTargetSpeed = 0;
        }
      }
    }

    // Pedestrian target speed
    if (pedRequiresStop) {
      isYieldingNow = true;
      if (distToPed <= 6.0) {
        mustFullStop = true;
        calculatedTargetSpeed = 0;
      } else {
        const t = Math.max(0, (distToPed - 6.0) / (16.0 - 6.0));
        calculatedTargetSpeed = Math.min(calculatedTargetSpeed, this.cruiseSpeedKmh * (t * t));
        if (calculatedTargetSpeed < 3.0) {
          mustFullStop = true;
          calculatedTargetSpeed = 0;
        }
      }
    }

    // Player obstacle target speed (only if player is ahead)
    if (playerPos && (isImminentAhead || playerInMyCorridor)) {
      isYieldingNow = true;
      const closingSpeedKmh = Math.max(1.0, this.speedKmh - playerSpeedKmh);
      const availableDist = Math.max(0, distToPlayer - this.PLAYER_STOP_DIST);
      const ttc = availableDist / (closingSpeedKmh / 3.6);

      if (playerSpeedKmh < 1.5 || isImminentAhead || distToPlayer <= this.PLAYER_STOP_DIST + 2.5 || ttc < 2.5) {
        mustFullStop = true;
        calculatedTargetSpeed = 0;
        if (distToPlayer <= this.PLAYER_STOP_DIST) {
          this.speedKmh = 0;
        }
      } else {
        const dangerDist = Math.max(this.PLAYER_DANGER_DIST, (this.cruiseSpeedKmh / 3.6) * 2.8);
        const t = availableDist / (dangerDist - this.PLAYER_STOP_DIST);
        calculatedTargetSpeed = Math.min(calculatedTargetSpeed, Math.max(0, Math.min(this.cruiseSpeedKmh * t, playerSpeedKmh)));
      }
    }

    this.isYielding = isYieldingNow;

    if (mustFullStop) {
      this.targetSpeedKmh = 0;
      if (this.speedKmh < 4.0) {
        this.speedKmh = 0;
      }
    } else if (isYieldingNow) {
      this.targetSpeedKmh = Math.max(0, calculatedTargetSpeed);
    } else {
      this.yieldWaitTimer = 0;
      this.targetSpeedKmh = Math.min(this.cruiseSpeedKmh, this.targetSpeedKmh + 10.0 * deltaTime);
    }

    // Smooth speed change: emergency braking rate 60 km/h/s, smooth accel 12 km/h/s
    const speedError = this.targetSpeedKmh - this.speedKmh;
    const changeRate = speedError < 0 ? 60.0 : 12.0;
    this.speedKmh += Math.sign(speedError) * Math.min(Math.abs(speedError), changeRate * deltaTime);
    this.speedKmh = Math.max(0, this.speedKmh);

    // ── Dynamic Brake Light & Turn Signal Visuals ────────────────────────────
    this.isBraking =
      this.isYielding ||
      mustFullStop ||
      speedError < -1.5 ||
      (this.speedKmh < 1.0 && this.targetSpeedKmh === 0);
    if (this.taillightMat) {
      this.taillightMat.emissiveIntensity = this.isBraking ? 4.5 : 1.2;
    }

    this.blinkTimer += deltaTime;
    if (this.blinkTimer >= 0.38) {
      this.blinkTimer = 0;
      this.blinkOn = !this.blinkOn;
    }
    const leftIntensity = this.blinkerState === 'left' && this.blinkOn ? 3.5 : 0.0;
    const rightIntensity = this.blinkerState === 'right' && this.blinkOn ? 3.5 : 0.0;
    for (const m of this.indicatorLeftMeshes) {
      (m.material as THREE.MeshStandardMaterial).emissiveIntensity = leftIntensity;
    }
    for (const m of this.indicatorRightMeshes) {
      (m.material as THREE.MeshStandardMaterial).emissiveIntensity = rightIntensity;
    }

    // Move along waypoint vector
    if (this.speedKmh > 0.1) {
      const speedMs = this.speedKmh / 3.6;
      let moveDist = speedMs * deltaTime;

      // Hard clamp: Never penetrate player's hull IF player is ahead
      if (playerPos && dotFront > 0.5) {
        const maxAllowed = Math.max(0, distToPlayer - this.PLAYER_STOP_DIST);
        moveDist = Math.min(moveDist, maxAllowed);
      }
      // Hard clamp: Never penetrate vehicle ahead in queue
      if (vehicleAheadRequiresStop && distToVehicleAhead <= targetStopBuffer - 0.2) {
        moveDist = 0;
      }
      // Hard clamp: Never cross traffic light stop line while RED
      if (lightRequiresStop && distToLight <= 5.0) {
        moveDist = 0;
      }
      // Hard clamp: Never hit crossing pedestrian ahead
      if (pedRequiresStop && distToPed <= 4.5) {
        moveDist = 0;
      }

      if (moveDist > 0.0001) {
        this.position.addScaledVector(dir, moveDist);
        this.mesh.position.copy(this.position);
      }

      // Orient towards movement direction
      if (dir.lengthSq() > 0.001) {
        const targetAngle = Math.atan2(dir.x, dir.z);
        const currentAngle = this.mesh.rotation.y;
        const angleDiff = ((targetAngle - currentAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        this.mesh.rotation.y += angleDiff * Math.min(1.0, 5.0 * deltaTime);
      }
    }

    // Synchronize physics body with kinematic movement
    if (this.body) {
      this.body.position.set(this.position.x, this.position.y, this.position.z);
      const speedMs = this.speedKmh / 3.6;
      this.body.velocity.set(dir.x * speedMs, 0, dir.z * speedMs);
      this.body.quaternion.setFromEuler(0, this.mesh.rotation.y, 0);
    }
  }
}

